import React from "react";
import { motion } from "framer-motion";
import { Star } from "lucide-react";
import { classes } from "./tokens";

/**
 * Testimonials — gomechanic-style horizontal-snap carousel with source
 * platform icons (Google / Facebook / Twitter) for trust amplification.
 * No JS carousel framework — pure CSS scroll-snap. Works on touch + desktop.
 */
const reviews = [
  {
    name: "Rohan Mehta", car: "Maruti Swift VXi · 2019",
    avatar: "https://images.unsplash.com/photo-1506794778202-cad84cf45f1d?auto=format&fit=crop&w=160&q=80",
    rating: 5, source: "google",
    text: "Doorstep pickup was on the dot. Got photos of the engine bay, oil change, and the inspection report on WhatsApp before delivery. Way more transparent than my old workshop.",
  },
  {
    name: "Priya Iyer", car: "Hyundai Creta · 2021",
    avatar: "https://images.unsplash.com/photo-1534528741775-53994a69daeb?auto=format&fit=crop&w=160&q=80",
    rating: 5, source: "facebook",
    text: "Honestly skeptical about ‘pay-after’ at first. But the price they quoted was the price I paid. No surprise add-ons, no upsells. Will book again.",
  },
  {
    name: "Arjun Reddy", car: "Tata Nexon · 2020",
    avatar: "https://images.unsplash.com/photo-1500648767791-00dcc994a43e?auto=format&fit=crop&w=160&q=80",
    rating: 5, source: "google",
    text: "Booked at 9am, picked up by 10:30, delivered same day at 6pm. AC was barely cooling before — vent cleaning made a real difference. Worth every rupee.",
  },
  {
    name: "Neha Sharma", car: "Honda City · 2018",
    avatar: "https://images.unsplash.com/photo-1438761681033-6461ffad8d80?auto=format&fit=crop&w=160&q=80",
    rating: 5, source: "twitter",
    text: "I always dreaded service day — endless calls about ‘extra repairs needed’. With Wisedrive, the quote was honest, the WhatsApp updates were constant, and the car came back better than I expected.",
  },
  {
    name: "Vikram Singh", car: "Mahindra XUV700 · 2023",
    avatar: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?auto=format&fit=crop&w=160&q=80",
    rating: 5, source: "google",
    text: "The 25-point digital report is a game-changer. I knew exactly what was checked, what was OK, and what to watch in 3-6 months. Very professional outfit.",
  },
];

// Brand-coloured source pill (no external SVG → reliable, lightweight).
const SourceBadge = ({ source }) => {
  const config = {
    google:   { label: "Google",   bg: "#FFFFFF", text: "#1A73E8", border: "#DADCE0", letter: "G" },
    facebook: { label: "Facebook", bg: "#1877F2", text: "#FFFFFF", border: "#1877F2", letter: "f" },
    twitter:  { label: "X",        bg: "#000000", text: "#FFFFFF", border: "#000000", letter: "X" },
  }[source] || { label: source, bg: "#F1F5F9", text: "#0F172A", border: "#E2E8F0", letter: "·" };
  return (
    <div
      className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md text-[10px] font-bold tracking-wider"
      style={{ backgroundColor: config.bg, color: config.text, border: `1px solid ${config.border}` }}
    >
      <span className="font-extrabold text-[11px]" style={{ fontFamily: "'Manrope', system-ui" }}>
        {config.letter}
      </span>
      {config.label}
    </div>
  );
};

const CarServiceTestimonials = () => (
  <section
    className="py-16 lg:py-24 bg-white"
    data-testid="testimonials-section"
  >
    <div className={classes.container}>
      <div className="flex items-end justify-between mb-8">
        <div>
          <span className={classes.overline}>What Our Customers Say</span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl tracking-tight font-extrabold text-slate-900 leading-tight mt-2">
            5,000 services done.<br />
            <span className="text-slate-500">3,200 verified reviews.</span>
          </h2>
        </div>
      </div>

      {/* Horizontal scroll carousel (CSS-only) */}
      <div
        className="flex gap-4 lg:gap-5 overflow-x-auto pb-6 -mx-5 px-5 lg:-mx-12 lg:px-12 scrollbar-hide snap-x snap-mandatory"
        data-testid="testimonials-carousel"
      >
        {reviews.map((r, i) => (
          <motion.figure
            key={r.name}
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: "-50px" }}
            transition={{ duration: 0.4, delay: i * 0.06 }}
            className="snap-start shrink-0 w-[300px] sm:w-[360px] bg-white border border-slate-200 rounded-2xl p-6 shadow-[0_8px_30px_rgb(0,0,0,0.04)] hover:shadow-[0_20px_40px_rgb(0,0,0,0.08)] transition-shadow flex flex-col"
          >
            <div className="flex items-start justify-between gap-3 mb-4">
              <div className="flex items-center gap-1">
                {[...Array(r.rating)].map((_, k) => (
                  <Star key={k} className="h-4 w-4 text-blue-500 fill-blue-500" />
                ))}
              </div>
              <SourceBadge source={r.source} />
            </div>
            <blockquote className="text-slate-700 leading-relaxed text-[15px] mb-5 flex-1">
              "{r.text}"
            </blockquote>
            <figcaption className="flex items-center gap-3 pt-4 border-t border-slate-100">
              <img
                src={r.avatar}
                alt={r.name}
                className="w-10 h-10 rounded-full object-cover bg-slate-200"
                loading="lazy"
              />
              <div>
                <div className="text-sm font-extrabold text-slate-900">{r.name}</div>
                <div className="text-[11px] text-slate-500 font-mono">{r.car}</div>
              </div>
            </figcaption>
          </motion.figure>
        ))}
      </div>

      <p className="text-xs text-slate-400 mt-2 px-1">← Swipe to see more reviews</p>
    </div>
  </section>
);

export default CarServiceTestimonials;
